import { useTheme } from '@mui/material';

import { colors, COLORS } from '@constants/colors';
import { ScreenSizes } from '@interfaces';

// Exported because the editor's dialogs are the same glass as its panels, and every one of them
// that spelled the values out again is a place a retune has to be remembered. See
// `confirmDialogPaper` in Inspector/styles.ts.
export const PANEL_BG =
  'linear-gradient(135deg, rgba(28, 28, 34, 0.88) 0%, rgba(16, 16, 20, 0.88) 100%)';
export const PANEL_BORDER = '1px solid rgba(255, 255, 255, 0.08)';
const PANEL_RADIUS = '14px';
export const PANEL_BLUR = 'blur(18px)';

// The editor's own scrollbar, so a scrolling pane never falls back to the OS-native bar — which
// reads as a duplicate next to the themed one, and at native width takes a tenth of a 360px panel.
//
// Defined here and imported by the tree rail rather than written twice: the two panes sit side by
// side, and two definitions of one look are exactly the pair that drifts apart.
export const editorScrollbar = {
  scrollbarWidth: 'thin' as const,
  scrollbarColor: '#FFFFFF4D transparent',
  '&::-webkit-scrollbar': {
    width: '8px',
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#FFFFFF4D',
    border: '2px solid transparent',
    backgroundClip: 'padding-box' as const,
    borderRadius: '18px',
  },
};

export const useEditorStyles = () => {
  const { breakpoints } = useTheme();

  return {
    glassPanel: {
      background: PANEL_BG,
      border: PANEL_BORDER,
      borderRadius: PANEL_RADIUS,
      backdropFilter: PANEL_BLUR,
      color: COLORS.WHITE,
      boxShadow: '0 14px 40px rgba(0, 0, 0, 0.35)',
    },

    root: {
      position: 'fixed',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 1200,
    },

    sceneTreeWrap: {
      position: 'absolute',
      top: '96px',
      bottom: '112px',
      left: '40px',
      width: '340px',
      pointerEvents: 'auto',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      gap: '10px',
    },
    // The Scene panel inside the wrap. It takes whatever height is left after the layout plaque, which
    // is sized by its own content — so a plaque that grows a line (the over-budget notice) shortens the
    // tree rather than pushing itself off the bottom of the screen.
    sceneTreePanel: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    sceneTreeCollapsedPill: {
      position: 'absolute',
      top: '96px',
      left: '40px',
      pointerEvents: 'auto',
      padding: '8px 14px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer',
      color: COLORS.WHITE,
      fontSize: '13px',
      fontWeight: 500,
      '&:hover': {
        background:
          'linear-gradient(135deg, rgba(38, 38, 46, 0.92) 0%, rgba(22, 22, 28, 0.92) 100%)',
      },
    },

    inspectorWrap: {
      position: 'absolute',
      top: '96px',
      // Hug the panel's height instead of spanning to a fixed bottom. A fixed
      // top + bottom made the wrapper taller than its content, so the empty
      // lower area showed the 3D scene through it and swallowed clicks meant for
      // the scene. maxHeight keeps the same scroll cap the panel itself uses
      // (calc(100vh - 208px) == old top 96px + bottom 112px).
      maxHeight: 'calc(100vh - 208px)',
      right: '40px',
      width: '360px',
      pointerEvents: 'auto',
      overflow: 'hidden',
      // Match the underlying inspector panel's corner radius so the wrapper's
      // overflow:hidden clips flush with the rounded panel instead of square.
      borderRadius: '20px',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    },

    // Sits on the top chrome row, between the location pill (top-left) and the
    // user controls (top-right): 32px matches MytaverseAppBar's VERTICAL_SPACE,
    // and the breakpoint override below matches its VERTICAL_SPACE_MOBILE — the
    // pill and the user controls both drop from top:32 to top:16 at this same
    // width, and a wrap that didn't follow sat 16px lower than its neighbours.
    // Bottom-center is avoided on purpose — it covered the avatar and the map
    // editor's room preview.
    brandingBarWrap: {
      position: 'absolute',
      top: '32px',
      left: '50%',
      transform: 'translateX(-50%)',
      pointerEvents: 'auto',
      [breakpoints.down(ScreenSizes.TabletHorizontal)]: {
        top: '16px',
      },
    },

    panelHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 14px 8px',
    },
    panelTitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: COLORS.WHITE,
    },
    panelTitleIcon: {
      fontSize: 18,
      color: colors.white_60,
    },
    panelTitle: {
      color: COLORS.WHITE,
      fontSize: '14px',
      fontWeight: 600,
    },
    panelSubtitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      marginLeft: '26px',
      minWidth: 0,
    },
    panelSubtitle: {
      color: colors.white_50,
      fontSize: '11px',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: 0,
    },
    panelSubtitleCopy: {
      color: colors.white_50,
      padding: '2px',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: COLORS.WHITE,
      },
    },
    panelSubtitleSize: {
      flexShrink: 0,
      color: colors.white_50,
      fontSize: '10px',
      fontWeight: 600,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      letterSpacing: '0.02em',
      whiteSpace: 'nowrap',
      padding: '1px 6px',
      borderRadius: '5px',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      border: '1px solid rgba(255, 255, 255, 0.12)',
    },
    panelIconButton: {
      color: colors.white_60,
      padding: '4px',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: COLORS.WHITE,
      },
    },
    panelBody: {
      padding: '4px 10px 12px',
      overflowY: 'auto',
      flex: 1,
      minHeight: 0,
      ...editorScrollbar,
    },
    // For a body that scrolls internally so its action bar can stay put: this
    // wrapper only hands down the height and never scrolls itself, and the
    // horizontal padding moves inside, where the scrolling pane and the bar can
    // each own it.
    panelBodyStatic: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
      paddingTop: '4px',
    },
  };
};
